"use client";
import React, { useRef, useEffect, useState } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";

import "../styles/Epc.css"; // Original Epc CSS
import "./../styles/Elecinstallation.css"; // CSS for ElecDesignHeroSection

import { Heropar } from "../components/Heropar";
import Beams from "./Beams"; // Assuming Beams is in the same directory or ./components/Beams

// Assets for Heropar
import img1 from "../assets/epc22A.jpeg";
import img2 from "../assets/epc-banner11A.jpeg";
import img3 from "../assets/sdaca13A.jpeg";
import img4 from "../assets/testing15A.jpeg";
import img5 from "../assets/epc-banner6A.jpg";
import img6 from "../assets/epc-collage8A.png.jpeg";
import img7 from "../assets/epc-banner4A.png";
import img8 from "../assets/manpower11A.jpeg";

// Assets for ElecDesignHeroSection timeline
import image7 from "../assets/epc5A.jpg";
import image8 from "../assets/scope1A.png"; 
import image9 from "../assets/epc-collage1A.png"; 
import image10 from "../assets/epc-collage2A.png"; 
import image11 from "../assets/epc-collage3A.png"; 
import image12 from "../assets/epc-collage4A.png"; 
import image13 from "../assets/epc-collage5A.png"; 

import image15 from "../assets/epc-collage7A.png"; 


const products = [
  { thumbnail: img1 },
  { thumbnail: img2 },
  { thumbnail: img3 },
  { thumbnail: img4 },
  { thumbnail: img5 },
  { thumbnail: img6 },
  { thumbnail: img7 },
  { thumbnail: img8 },
];

// --- ElecDesignHeroSection (formerly from EnggDesignHero.js) ---
const ElecDesignHeroSection = ({ data }) => {
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const [timelineHeight, setTimelineHeight] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    const calculateAndUpdateTimelineHeight = () => {
      if (timelineRef.current) {
        const newHeight = timelineRef.current.getBoundingClientRect().height;
        setTimelineHeight(prevHeight => {
          if (newHeight > 0 && (newHeight !== prevHeight || prevHeight === 0)) {
            return newHeight;
          }
          return prevHeight;
        });
      }
    };

    const handleLayoutReady = () => {
      calculateAndUpdateTimelineHeight();
      AOS.refresh(); // Refresh AOS after layout calculation
    };

    // Initial calculation attempt
    const initialCalcTimer = setTimeout(calculateAndUpdateTimelineHeight, 50);

    if (document.readyState === "complete") {
      const readyTimer = setTimeout(handleLayoutReady, 100); // Give a bit more time for layout
      return () => clearTimeout(readyTimer);
    } else {
      window.addEventListener("load", handleLayoutReady);
    }

    window.addEventListener("resize", handleLayoutReady);

    // Fallback refresh for AOS if layout changes affect it
    const aosRefreshInterval = setInterval(() => {
        if (timelineRef.current) { // Check if component is still mounted
            AOS.refresh();
        } else {
            clearInterval(aosRefreshInterval);
        }
    }, 1000);


    return () => {
      clearTimeout(initialCalcTimer);
      window.removeEventListener("load", handleLayoutReady);
      window.removeEventListener("resize", handleLayoutReady);
      clearInterval(aosRefreshInterval);
    };
  }, [data]); // Re-run if data changes, which might affect layout

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"], // Adjusted offset slightly, might need tuning
  });

  const heightTransform = useTransform(
    scrollYProgress,
    [0, 1],
    [0, timelineHeight]
  );
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <>
<div className="elec-design-hero-container" ref={containerRef}>
      <div className="elec-design-hero-header">
        <h2 className="elec-design-hero-title">Turnkey EPC Solutions</h2> {/* Adjusted title for context */}
        <p className="elec-design-hero-description">
          Key phases in our Epc Solution.
        </p>
      </div>

      <div ref={timelineRef} className="elec-design-hero-timeline">
        {data.map((item, index) => (
          <div key={index} className="elec-design-hero-step">
            <div className="elec-design-hero-sticky">
              <div className="elec-design-hero-marker-outer">
                <div className="elec-design-hero-marker-inner" />
              </div>
              <h3 className="elec-design-hero-step-title-desktop">{item.title}</h3>
            </div>

            <div className="elec-design-hero-step-content">
              <h3 className="elec-design-hero-step-title-mobile">{item.title}</h3>
              <img
                src={item.image}
                alt={item.title}
                className="elec-design-hero-step-image epc-img1"
                data-aos="fade-left"
              />
              <div className="elec-design-hero-step-paragraph " data-aos="fade-left">
                {typeof item.paragraph === "string" ? (
                  <p>{item.paragraph}</p>
                ) : (
                  <>
                    <p>{item.paragraph?.text}</p>
                    {Array.isArray(item.paragraph?.points) &&
                      item.paragraph.points.map((point, idx) => (
                        <p key={idx} className="elec-design-hero-step-point epc-p1">
                          <span className="elec-design-hero-arrow-icon">→</span> {point}
                        </p>
                      ))}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

        {timelineHeight > 0 && ( // Only render progress bar if timelineHeight is calculated
          <div
            style={{ height: `${timelineHeight}px` }} // Ensure this has a valid height
            className="elec-design-hero-progress-bar-wrapper"
          >
            <motion.div
              style={{
                height: heightTransform,
                opacity: opacityTransform,
              }}
              className="elec-design-hero-progress-bar"
            />
          </div>
        )}
      </div>
    </div>
    </>
    
  );
};

// Data for the ElecDesignHeroSection timeline
const timelineData = [
  {
    title: "Where we are",
    image: image7, // Use the renamed import
    paragraph: {
      points: [
        "ACUME offers entire turnkey EPC solutions starting from design, engineering, supply, project management, erection, testing, commissioning and maintenance of AIS and GIS substations for Power, Oil & Gas and Infrastructure sectors from 11kV to 400kV level.",
      ]
      }
  },
  {
    title: "Our scope of work",
    image: image8, // Use the renamed import
    paragraph: {
     points: [
"Designs that transform complex engineering concepts into accurate, construction-ready drawings aligned with global standards.",
"Strategic procurement solutions that ensure timely sourcing of quality-approved materials and equipment, optimizing cost-efficiency and project timelines.",
"Expert installation services that guarantee seamless integration, safety compliance, and optimal performance of all systems and equipment on-site.",
"Through commissioning processes that validate system functionality and reliability, ensuring safe and efficient operation from day one.",
"Proactive maintenance services that maximize equipment uptime, extend asset life, and prevent costly breakdowns through regular inspections and timely repairs.",

]
    }
  },
  {
    title: "Complete Design & Engineering.",
    image: image9, // Use the renamed import
    paragraph: {
      points: [
        "We provide end-to-end AutoCAD Design & Engineering Services tailored to meet the complex demands of modern infrastructure, industrial, and commercial projects. Our expert team specializes in delivering precise detailed engineering drawings that align with global standards. From conceptual design to final documentation, we ensure technical accuracy, cost efficiency, and seamless project integration. Whether it's electrical schematics, mechanical assemblies, piping layouts, or civil structures, our AutoCAD-driven solutions help clients visualize, plan, and execute their ideas with unmatched clarity and precision.",
      ]
      }
  },
   {
    title: "Total civil construction, foundations of HV & EHV equipment",
    image: image10, // Use the renamed import
    paragraph: {
      points: [
        "We offer comprehensive civil construction solutions specializing in the design and execution of foundations for High Voltage (HV) and Extra High Voltage (EHV) equipment. Our services encompass site grading, excavation, formwork, reinforced concrete foundations, cable trenches, and equipment pedestals, ensuring structural stability and long-term performance. With a focus on precision, safety, and industry compliance, we support substations, switchyards, and power infrastructure with robust civil works tailored to withstand dynamic loads and harsh environmental conditions. From planning to handover, our civil engineering team ensures seamless integration of foundation systems with the mechanical and electrical layout, delivering reliable support for critical power assets.",
      ]
      }
  },
  {
    title: "Total Infrastructure Development",
    image: image11, // Use the renamed import
    paragraph: {
      points: [
        "We deliver fully integrated Infrastructure Development Services that take projects from raw land to operational excellence. Our multidisciplinary team orchestrates every phase—master planning, site preparation, utilities, road networks, structural works, and finishing civil amenities—ensuring seamless coordination and adherence to international standards. Leveraging advanced surveying, BIM-enabled design, and rigorous project management, we optimize timelines, control costs, and minimize environmental impact. From industrial parks and renewable-energy facilities to smart cities and transportation hubs, our turnkey approach provides clients with resilient, future-ready infrastructure that supports sustainable growth and long-term asset value.",
      ]
      }
  },
   {
    title: "Supply & installation of all equipment including SCADA.",
    image: image12, // Use the renamed import
    paragraph: {
      points: [
        "We offer end-to-end services for the supply and installation of all electrical and control equipment, including advanced SCADA systems. Our solutions cover procurement, delivery, erection, testing, and commissioning of transformers, switchgear, control panels, protection systems, and communication networks. With a strong focus on precision and integration, we ensure that each component is installed to meet performance, safety, and regulatory standards. Our SCADA services enable real-time monitoring, remote control, and efficient data acquisition across substations and industrial facilities, enhancing operational visibility and reliability. Backed by skilled engineers and industry-leading practices, we deliver turnkey systems that are scalable, intelligent, and built for long-term efficiency.",
      ]
      }
  },
  {
    title: "Charging & Battery bank installation",
    image: image13, // Use the renamed import
    paragraph: {
      points: [
        "We provide reliable Backup Power Solutions and Battery Bank Installation Services to ensure uninterrupted power for critical operations. Our scope includes the supply, installation, and commissioning of high-capacity sets and advanced battery backup systems tailored to your load requirements. From foundation work and cabling to synchronization, control panels, and exhaust systems, every installation is executed with strict adherence to safety and efficiency standards. Our battery banks, including VRLA and Li-ion technologies, are designed for optimal backup duration and seamless integration with UPS and SCADA systems. Whether for industrial facilities, data centers, or substations, we deliver dependable, fail-safe power continuity solutions that keep your operations running smoothly under any circumstance.",
      ]
      }
  },
  {
    title: "Project management, T&C and O&M.",
    image: image15, // Use the renamed import
    paragraph: {
      points: [
        "We provide comprehensive Project Management, Testing & Commissioning (T&C), and Operations & Maintenance (O&M) services to ensure the successful execution and long-term performance of infrastructure and energy projects. Our project management team oversees every phase—from planning and procurement to execution and handover—ensuring timelines, budgets, and quality benchmarks are met with precision. Through meticulous Testing & Commissioning, we validate the functionality, safety, and reliability of all installed systems before activation. Our O&M services offer ongoing technical support, preventive maintenance, performance optimization, and rapid troubleshooting to maximize uptime and asset lifespan. With a commitment to excellence, compliance, and innovation, we ensure your projects are delivered efficiently and operate seamlessly for years to come.",
      ]
      }
  },
];


const Epc = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const form = useRef(); // This ref is currently unused in the Epc component itself

  return (
    <div className="epc-page-wrapper"> {/* Added a wrapper for potentially better styling control */}
      <div className="epc-container">
        <div className="epc-beam-background">
           <Beams
            beamWidth={1.4}
            beamHeight={30}
            beamNumber={14}
            lightColor="#00CCFF"
            speed={20}
            noiseIntensity={0}
            scale={0.19}
            rotation={59}
          />
        </div>
        
        <Heropar
          products={products}
          heading="EPC Contracting"
          description="Our EPC (Engineering, Procurement, and Construction) solutions deliver end-to-end project execution with precision, speed, and innovation."
        />
      </div>

      {/* Integrated ElecDesignHeroSection below Heropar */}
      <ElecDesignHeroSection data={timelineData} />
    </div>
  );
};

export default Epc;